
import React, { useState, useEffect } from 'react';
import { X, Save, Building2, MapPin, Globe, StickyNote } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { Organization } from '../../types';

interface OrganizationFormModalProps {
  organization?: Organization | null;
  onClose: () => void;
}

export const OrganizationFormModal: React.FC<OrganizationFormModalProps> = ({ organization, onClose }) => {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  
  const [formData, setFormData] = useState({
    name: '',
    org_type_id: '',
    website: '',
    street_address: '',
    city: '',
    state: '',
    zip_code: '',
    notes: ''
  });

  const { data: orgTypes } = useQuery({
    queryKey: ['org-types-lookup'],
    queryFn: async () => (await supabase.from('organization_types').select('*')).data || []
  });

  useEffect(() => {
    if (organization) {
      setFormData({
        name: organization.name || '',
        org_type_id: organization.org_type_id || '',
        website: organization.website || '',
        street_address: organization.street_address || '',
        city: organization.city || '',
        state: organization.state || '',
        zip_code: organization.zip_code || '',
        notes: organization.notes || ''
      });
    }
  }, [organization]);

  const upsertMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const payload = {
        ...data,
        created_by_email: profile?.email
      };

      if (organization) {
        const { error } = await supabase.from('organizations').update(payload).eq('id', organization.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('organizations').insert([payload]);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      queryClient.invalidateQueries({ queryKey: ['organization-base', organization?.id] });
      onClose();
    }
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white w-full max-w-xl rounded-md shadow-2xl border border-slate-200 overflow-hidden flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-200">
        <div className="px-8 py-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <h2 className="text-xl font-black uppercase text-slate-900 tracking-tight flex items-center gap-3">
            <Building2 size={22} className="text-blue-600" />
            {organization ? 'Update Organization' : 'New Organization'}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900 transition-colors">
            <X size={24} />
          </button>
        </div>

        <form 
          className="p-8 space-y-6 overflow-y-auto"
          onSubmit={(e) => {
            e.preventDefault();
            upsertMutation.mutate(formData);
          }}
        >
          <Input label="Organization Name" required value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-1.5">
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Organization Type</label>
              <select 
                className="w-full px-5 py-3 bg-white border border-slate-200 rounded-lg font-bold text-slate-900 text-sm outline-none focus:ring-4 focus:ring-slate-100 transition-all"
                value={formData.org_type_id}
                onChange={e => setFormData({...formData, org_type_id: e.target.value})}
                required
              >
                <option value="">Select Category...</option>
                {orgTypes?.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
            <Input label="Website" value={formData.website} onChange={e => setFormData({...formData, website: e.target.value})} placeholder="https://hfli.org" />
          </div>

          <div className="space-y-4 pt-2">
            <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
              <MapPin size={12} /> Address
            </h3>
            <Input label="Street Address" value={formData.street_address} onChange={e => setFormData({...formData, street_address: e.target.value})} placeholder="123 Example Way" />
            <div className="grid grid-cols-3 gap-4">
               <div className="col-span-1">
                 <Input label="City" value={formData.city} onChange={e => setFormData({...formData, city: e.target.value})} />
               </div>
               <div>
                 <Input label="State" value={formData.state} onChange={e => setFormData({...formData, state: e.target.value})} placeholder="MI" />
               </div>
               <div>
                 <Input label="Zip Code" value={formData.zip_code} onChange={e => setFormData({...formData, zip_code: e.target.value})} />
               </div>
            </div>
          </div>

          <Input label="Notes" isTextArea rows={3} value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} placeholder="Internal organization notes..." />

          <div className="flex gap-4 pt-4">
             <Button type="button" variant="ghost" className="flex-1" onClick={onClose}>Discard</Button>
             <Button type="submit" className="flex-[2] py-4 shadow-lg" isLoading={upsertMutation.isPending} icon={<Save size={18} />}>
               Save Organization
             </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
